import Modal from "react-modal";
import { useEffect, useState } from "react";
import { IoCheckmarkSharp } from "react-icons/io5";
import { IoCloseSharp } from "react-icons/io5";

const CEPModal = ({
  isModalOpen,
  setModalOpen,
  restaurantServesAtAddress,
  onConfirmCep,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCepChange = (event) => {
    const cepValue = event.target.value ?? "";

    if (cepValue.length > 9) {
      return;
    }

    const cleared = cepValue.replaceAll(/\D/g, "");
    let value = cleared;

    if (cleared.length > 5) {
      value = `${cleared.slice(0, 5)}-${cleared.slice(5)}`;
    }

    setInputValue(value);
    setError("");
  };

  const handleModalConfirm = () => {
    if (loading) {
      return;
    }

    const cleared = inputValue.replace(/\D/g, "");

    if (cleared.length !== 8) {
      return setError("Insira um CEP válido");
    }

    setLoading(true);
    onConfirmCep(cleared).finally(() => {
      setLoading(false);

      if (restaurantServesAtAddress === false) {
        setModalOpen(false);
      }
    });
  };

  function getCepViaReverseLocation() {
    if (!isModalOpen) {
      return;
    }

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        setLoading(true);

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`,
          );
          const data = await response.json();
          const cep = data.address.postcode;

          if (cep) {
            setInputValue(cep);
          }
        } catch (error) {
          console.error("Erro ao buscar o CEP:", error);
        } finally {
          setLoading(false);
        }
      });
    }
  }

  useEffect(getCepViaReverseLocation, [isModalOpen]);

  return (
    <Modal
      isOpen={isModalOpen}
      ariaHideApp={false}
      className="rounded-lg fixed bg-secondary-500 max-w-md w-[90vw] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6"
    >
      {restaurantServesAtAddress === null ? (
        <div className="max-w-md mx-auto text-center">
          <p className="text-black/90 leading text-lg mb-6">
            <b>Informe seu CEP</b> para verificar a disponibilidade do
            restaurante na região
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <input
              value={inputValue}
              type="text"
              placeholder={loading ? "Buscando CEP..." : "Digite seu CEP"}
              name="cep"
              id="cep"
              autoFocus
              autoComplete="postal-code"
              onChange={handleCepChange}
              disabled={loading}
              className="px-4 py-2 rounded-lg w-48 bg-white text-black focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />

            <button
              onClick={handleModalConfirm}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-primary-500 text-white font-bold hover:bg-primary-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirmar
            </button>
          </div>

          {error && (
            <p className="text-red-400 mt-4 text-sm font-semibold">{error}</p>
          )}
        </div>
      ) : restaurantServesAtAddress ? (
        <RestaurantAvailableState setModalOpen={setModalOpen} />
      ) : (
        <RestaurantNotAvailableState setModalOpen={setModalOpen} />
      )}
    </Modal>
  );
};

const AvailableState = ({
  setModalOpen,
  title,
  description = undefined,
  timeout = 2000,
  children,
}) => {
  useEffect(() => {
    const timeoutCode = setTimeout(() => {
      setModalOpen(false);
    }, timeout);

    return () => clearTimeout(timeoutCode);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <IoCloseSharp
        size={30}
        role="button"
        onClick={() => setModalOpen(false)}
        className="absolute top-0 right-0 cursor-pointer"
      />

      <div className="flex items-center">
        {children}
        <p className="my-0 text-red-500 text-lg font-bold">{title}</p>
      </div>
      {description && (
        <p className="text-black/90 leading text-lg">{description}</p>
      )}
    </div>
  );
};

const RestaurantAvailableState = ({ setModalOpen }) => {
  return (
    <AvailableState title="Restaurante disponível" setModalOpen={setModalOpen}>
      <IoCheckmarkSharp color="green" size={30} />
    </AvailableState>
  );
};

const RestaurantNotAvailableState = ({ setModalOpen }) => {
  return (
    <AvailableState
      title="Restaurante indisponível"
      description="Este restaurante não está disponível nesse CEP. Você ainda pode visualizar o catálogo de produtos."
      timeout={5000}
      setModalOpen={setModalOpen}
    >
      <IoCloseSharp color="red" size={30} />
    </AvailableState>
  );
};

export default CEPModal;
